import { PlaylistAddCheckOutlined, StopCircleOutlined } from '@mui/icons-material'
import { Box, Stack, Typography } from '@mui/material'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/inputs/CustomButton'
import type { ResourceSetTestActions } from '@/features/prequalification/localControl/components/resourceSetTestActions'
import { marketKey } from '@/features/prequalification/localControl/components/resourceSetTestActions'
import RunTestButton from '@/features/prequalification/localControl/components/RunTestButton'
import TestRow from '@/features/prequalification/localControl/components/TestRow'
import type {
  LocalPqRun,
  PqMarketCatalogueEntry,
  ResourceSetMember,
} from '@/features/prequalification/localControl/types'
import { runsInMarket } from '@/features/prequalification/localControl/utils/marketCatalogue'
import { isTerminalPhase } from '@/features/prequalification/types'

type MarketTestListProps = {
  market: PqMarketCatalogueEntry
  resources: ResourceSetMember[]
  /** All resource set runs; the list scopes them to this market. */
  runs: LocalPqRun[]
  actions: ResourceSetTestActions
}

function MarketTestList({ market, resources, runs, actions }: Readonly<MarketTestListProps>) {
  const { t } = useTranslation()

  const marketRuns = runsInMarket(runs, market)
  const hasActiveRun = marketRuns.some((run) => !isTerminalPhase(run.phase))
  const isPending = actions.pendingKeys.has(marketKey(market.market))

  // FCR-N's one group repeats the market's label, so it renders flat. A sole group with a
  // label of its own still gets its subheader.
  const showSubheaders = market.groups.length > 1 || market.groups[0]?.label !== market.label

  return (
    <Stack sx={{ gap: 1 }}>
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1,
          flexWrap: 'wrap',
          px: 1,
        }}
      >
        <RunTestButton
          disabled={isPending || resources.length === 0}
          label={t('prequalifications.resource_set.tests.run_all')}
          startIcon={<PlaylistAddCheckOutlined />}
          testId="run-all-tests-button"
          onRun={(startAt) => actions.onRunMarket(market, startAt)}
        />
        {hasActiveRun && (
          <CustomButton
            color="error"
            disabled={isPending}
            size="small"
            startIcon={<StopCircleOutlined />}
            variant="outlined"
            onClick={() => actions.onStopMarket(market)}
          >
            {t('prequalifications.resource_set.tests.stop_all')}
          </CustomButton>
        )}
      </Stack>

      <Box>
        {market.groups.map((group) => (
          <Fragment key={group.label}>
            {showSubheaders && (
              <Typography
                color="text.secondary"
                sx={{ mt: 2, px: 1, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}
                variant="caption"
              >
                {group.label}
              </Typography>
            )}
            {group.tests.map((test) => (
              <TestRow
                key={test.testName}
                actions={actions}
                label={test.label}
                resources={resources}
                runs={marketRuns}
                testName={test.testName}
              />
            ))}
          </Fragment>
        ))}
      </Box>
    </Stack>
  )
}

export default MarketTestList
